import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs';
import { of } from 'rxjs';
import { BusinessService } from '../../../services/business';
import { BaseAuthComponent } from '../../common/BaseAuthComponent.class';
import { Client } from '../../interfaces';

@Component({
  selector: 'app-client-lookup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './client-lookup.html',
  styleUrl: './client-lookup.css',
})
export class ClientLookup extends BaseAuthComponent implements OnInit, OnChanges {
  @Input() label = 'Cliente';
  @Input() showMiniCard = false;
  @Input() placeholder = 'Busca por nombres, email, teléfono';
  @Input() selectedClient: Client | null = null;
  @Output() clientSelected = new EventEmitter<Client>();
  @Output() clientCleared = new EventEmitter<void>();

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown', { static: false }) dropdown?: ElementRef<HTMLDivElement>;

  private businessService = inject(BusinessService);
  private fb = inject(FormBuilder);

  searchForm = this.fb.group({
    searchTerm: [''],
  });

  get searchTermControl(): FormControl<string | null> {
    return this.searchForm.get('searchTerm') as FormControl<string | null>;
  }

  searchResults = signal<Client[]>([]);
  isSearching = signal(false);
  isDropdownOpen = signal(false);
  selectedClientSignal = signal<Client | null>(null);

  constructor() {
    super();
    this.setupSearch();
  }

  ngOnInit(): void {
    if (this.selectedClient) {
      this.selectedClientSignal.set(this.selectedClient);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedClient']) {
      if (this.selectedClient) {
        this.selectedClientSignal.set(this.selectedClient);
      } else {
        this.selectedClientSignal.set(null);
      }
    }
  }

  private setupSearch(): void {
    this.searchForm
      .get('searchTerm')
      ?.valueChanges.pipe(
        debounceTime(250),
        map((v) => (v ?? '').trim()),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.length < 2) {
            this.isSearching.set(false);
            this.searchResults.set([]);
            return of([]);
          }
          this.isSearching.set(true);
          this.isDropdownOpen.set(true);
          return this.businessService
            .searchEntityByBusinessId(this.businessId, 'clients', q, 1, 20)
            .pipe(
              catchError(() => {
                this.isSearching.set(false);
                return of([]);
              })
            );
        })
      )
      .subscribe({
        next: (results) => {
          this.searchResults.set(results);
          this.isSearching.set(false);
        },
        error: () => {
          this.isSearching.set(false);
          this.searchResults.set([]);
        },
      });
  }

  onSearchFocus(): void {
    const searchTerm = this.searchForm.get('searchTerm')?.value?.trim() || '';
    if (searchTerm.length >= 2 && this.searchResults().length > 0) {
      this.isDropdownOpen.set(true);
    }
  }

  onSearchInput(): void {
    const searchTerm = this.searchForm.get('searchTerm')?.value?.trim() || '';
    if (searchTerm.length >= 2) {
      this.isDropdownOpen.set(true);
    } else {
      this.isDropdownOpen.set(false);
    }
  }

  selectClient(client: Client): void {
    this.selectedClientSignal.set(client);
    this.searchForm.patchValue({ searchTerm: '' }, { emitEvent: false });
    this.isDropdownOpen.set(false);
    this.searchResults.set([]);
    this.clientSelected.emit(client);
  }

  clearClient(): void {
    this.selectedClientSignal.set(null);
    this.searchForm.patchValue({ searchTerm: '' });
    this.isDropdownOpen.set(false);
    this.searchResults.set([]);
    this.clientCleared.emit();
  }

  getClientDisplayName(client: Client | null): string {
    if (!client) return '';
    if (client.firstName && client.lastName) {
      return `${client.firstName} ${client.lastName}`;
    }
    return client.clientName || client.email || '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.dropdown?.nativeElement &&
      !this.dropdown.nativeElement.contains(event.target as Node) &&
      this.searchInput?.nativeElement &&
      !this.searchInput.nativeElement.contains(event.target as Node)
    ) {
      this.isDropdownOpen.set(false);
    }
  }
}

